// @flow

import { get } from 'lodash';
import { resolve, reject } from 'redux-optimist-promise';

import { QUERY_DEPARTURE_DATE, QUERY_DATE_FORMAT } from 'Utils/uri/constants';
import {
  FETCH_CALENDAR,
  CALENDAR_CHANGE_MONTH,
  CLEAR_CALENDAR,
  TRIP_INFORMATION_SET_DEPARTURE_DATE,
  SYNC_WITH_LOCATION,
} from 'Store/constants';
import createReducer from 'Store/utils/createReducer';
import {
  INITIAL_MONTH,
  getDefaultSelectedMonth,
  getPricesByDays,
  getSelectedMonth,
  type CalendarData,
  type PricesByDays,
} from 'Store/calendar/calendar.helper';

type CalendarRequestResolvedAction = OptimisticResolvedAction<{
  calendar: CalendarData,
}>;

export type CalendarState = {
  byDays: PricesByDays,
  basePrice: ProductPrice | null,
  calendar: CalendarData,
  errorResponse: { errors?: * },
  isLoading: boolean,
  lastRequestId: number,
  selectedMonth: number,
};

export const initialState: CalendarState = {
  basePrice: null,
  byDays: {},
  calendar: {},
  isLoading: true,
  errorResponse: {},
  selectedMonth: getDefaultSelectedMonth(),
  lastRequestId: -1,
};

export const calendarReducer = createReducer(initialState, {
  [SYNC_WITH_LOCATION](
    state: CalendarState,
    action: { payload: LocationChangePayload },
  ): CalendarState {
    const {
      payload: {
        location: { queryParams = {} },
      },
    } = action;

    if (!queryParams[QUERY_DEPARTURE_DATE]) {
      return state;
    }

    const selectedMonth = getDefaultSelectedMonth(
      queryParams[QUERY_DEPARTURE_DATE] || state.selectedMonth,
      QUERY_DATE_FORMAT,
    );

    return {
      ...state,
      selectedMonth,
    };
  },
  [FETCH_CALENDAR](state: CalendarState, action: OptimisticAction<{}, {}>): CalendarState {
    return {
      ...state,
      isLoading: true,
      lastRequestId: get(action, 'optimist.id', -1),
    };
  },
  [resolve(FETCH_CALENDAR)](
    state: CalendarState,
    action: CalendarRequestResolvedAction,
  ): CalendarState {
    const {
      optimist: { id = -1 } = {},
      payload: { calendar },
    } = action;
    // If there is another request in progress
    if (id !== state.lastRequestId || !calendar) {
      return state;
    }

    const { selectedMonth } = state;

    return {
      ...state,
      basePrice: calendar.basePrice,
      byDays: getPricesByDays(calendar),
      calendar,
      selectedMonth: getSelectedMonth(calendar, selectedMonth),
      isLoading: false,
    };
  },
  [reject(FETCH_CALENDAR)](state: CalendarState, action: OptimisticRejectedAction): CalendarState {
    const errors = action.payload.errors || '';

    return {
      ...state,
      byDays: {},
      errorResponse: { errors },
      isLoading: false,
    };
  },
  [CALENDAR_CHANGE_MONTH](
    state: CalendarState,
    action: Action<{ selectedMonth: number }>,
  ): CalendarState {
    const selectedMonth = action.payload.selectedMonth || INITIAL_MONTH;
    return {
      ...state,
      selectedMonth,
    };
  },
  [CLEAR_CALENDAR](): CalendarState {
    return initialState;
  },
  [TRIP_INFORMATION_SET_DEPARTURE_DATE](
    state: CalendarState,
    action: Action<{ departureDate: string }>,
  ): CalendarState {
    if (!action.payload.departureDate) {
      return state;
    }

    const selectedMonth = getDefaultSelectedMonth(action.payload.departureDate);

    return {
      ...state,
      selectedMonth,
    };
  },
});
